import { useState, type FormEvent } from 'react';
import { Field } from '../ui/Field';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { useAdmin } from '../AdminContext';
import { useCan } from '../useCan';
import { ADMIN_STEP_MINUTES, DEFAULT_STAFF_ID, conflictsFor } from '../../core/rules/agenda';
import { salonDayFor, staffList } from '../../core/rules/salonDays';
import { isIsoDate } from '../../core/format/dates';
import { useRequestId } from '../../hooks/useRequestId';
import { plural } from '../../core/format/text';
import { blockDurations, blockReasons, durationLabel } from './agendaRules';
import type { AgendaAppointment, AgendaBlock, AgendaBlockRequest, BlockReason } from '../../core/types/agenda';
type Props = {
  initialDate: string;
  initialTime: string;
  appointments: AgendaAppointment[];
  onSubmit: (req: AgendaBlockRequest) => Promise<AgendaBlock | null>;
  onDone: () => void;
  onCancel: () => void;
};

/** Tijd blokkeren: dan kan niemand die tijd boeken, ook niet via de webshop. */
export function BlockForm({
  initialDate,
  initialTime,
  appointments,
  onSubmit,
  onDone,
  onCancel
}: Props) {
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [durationMinutes, setDuration] = useState(60);
  const [reason, setReason] = useState<BlockReason>('privé');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const {
    site
  } = useAdmin();
  const viewer = useCan().viewer('agenda.view');
  const team = staffList(site.staff).filter(s => s.active);
  // Sarah kiest voor wie; een collega blokkeert altijd haar eigen tijd. Standaard: wie die dag werkt.
  const dayStaff = isIsoDate(date) && salonDayFor(site.openingHours, site.salonExceptions, date)?.staffId || DEFAULT_STAFF_ID;
  const [chosenStaff, setChosenStaff] = useState<string | null>(null);
  const staffId = viewer.isOwner ? chosenStaff ?? dayStaff : viewer.staffId;
  const requestId = useRequestId(JSON.stringify([staffId, date, time, durationMinutes, reason, label.trim()]));

  // Blokkeren over een bestaande afspraak heen mag (ze blijft staan), maar zeg het wel. Alleen
  // afspraken van deze medewerker tellen: een blokkade van Mila botst niet met Sarahs klant.
  const overlapping = time ? conflictsFor({
    date,
    time,
    durationMinutes,
    staffId
  }, appointments, [], {
    buffer: 0
  }).length : 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!date || !time || submitting) return;
    setSubmitting(true);
    const b = await onSubmit({
      requestId,
      staffId,
      date,
      time,
      durationMinutes,
      reason,
      ...(label.trim() ? {
        label: label.trim()
      } : {})
    });
    setSubmitting(false);
    if (b) onDone();
  };
  return <form onSubmit={submit} className="space-y-5" data-id="element-342" data-mp="38mljishfpp4r">
      <div data-id="element-343" data-mp="38lafqsnzohdn">
        <h2 className="font-display text-[1.4rem] font-light text-ink" data-id="element-344" data-mp="38jpn58l8j8if">Tijd blokkeren</h2>
        <p className="mt-0.5 text-[13px] text-muted" data-id="element-345" data-mp="19b2cijfoyqxq">Deze tijd verdwijnt meteen uit de vrije tijden op de website.</p>
      </div>

      <div role="radiogroup" aria-label="Reden" className="flex flex-wrap gap-1.5" data-id="element-346" data-mp="16o9qhi6k92kp">
        {blockReasons.map(r => <button key={r.value} type="button" role="radio" aria-checked={reason === r.value} onClick={() => setReason(r.value)} className={`rounded-full px-3.5 py-1.5 text-[13px] transition-colors duration-150 ease-soft ${reason === r.value ? 'bg-ink text-shell' : 'bg-sand text-ink hover:bg-blush'}`} data-id="element-347" data-mp="11xjidx6r2lmb">
            {r.label}
          </button>)}
      </div>

      <div className="grid gap-3 sm:grid-cols-3" data-id="element-348" data-mp="1km7zs960y8tc">
        <Field label="Datum" data-id="element-349" data-mp="1fog89g9akopo">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls} data-id="element-350" data-mp="1fnen2iykalu8" />
        </Field>
        <Field label="Vanaf" data-id="element-351" data-mp="1bpz1ixts9g8s">
          <input type="time" step={ADMIN_STEP_MINUTES * 60} value={time} onChange={e => setTime(e.target.value)} className={inputCls} data-id="element-352" data-mp="4aklblc5bbnw" />
        </Field>
        <Field label="Hoe lang" data-id="element-353" data-mp="7662ww16zbi">
          <select value={durationMinutes} onChange={e => setDuration(Number(e.target.value))} className={inputCls} data-id="element-354" data-mp="1o5f9iqvn67zf">
            {blockDurations.map(m => <option key={m} value={m} data-id="element-355" data-mp="1sx6sogcgxbfj">
                {durationLabel(m)}
              </option>)}
          </select>
        </Field>
      </div>

      {viewer.isOwner && team.length > 1 && <Field label="Voor wie" hint="Alleen de agenda van deze persoon gaat dicht." data-id="element-356" data-mp="1j3zqt5gxsdk7">
          <select value={staffId} onChange={e => setChosenStaff(e.target.value)} className={inputCls} data-id="element-357" data-mp="26wg6xah45vgv">
            {team.map(s => <option key={s.id} value={s.id} data-id="element-358" data-mp="1xv8lhszp4p47">
                {s.name}
              </option>)}
          </select>
        </Field>}

      <Field label="Omschrijving" hint="Optioneel, alleen voor jou. Bijvoorbeeld 'Tandarts' of 'Training emerginC'." data-id="element-359" data-mp="21uz6th6ged1c">
        <input value={label} onChange={e => setLabel(e.target.value)} maxLength={60} className={inputCls} data-id="element-360" data-mp="mhwdr70tfmcr" />
      </Field>

      {overlapping > 0 && <p role="status" className="rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" data-id="element-361" data-mp="ix2w1uzc5k7g">
          Hier {overlapping === 1 ? 'staat al een afspraak' : `staan al ${plural(overlapping, 'afspraak', 'afspraken')}`}. Die blijft staan; verzet of annuleer hem apart als dat nodig is.
        </p>}

      <div className="flex flex-wrap items-center gap-2" data-id="element-362" data-mp="ny0g6d3woobl">
        <button type="submit" disabled={!date || !time || submitting} aria-busy={submitting} className={btnPrimary} data-id="element-363" data-mp="7e6yii60y00">
          {submitting ? 'Blokkeren…' : 'Blokkeren'}
        </button>
        <button type="button" onClick={onCancel} className={btnGhost} data-id="element-364" data-mp="3rbl3buhgcvq0">
          Annuleer
        </button>
      </div>
    </form>;
}